import { useState, useCallback } from "react";

interface ShareTarget {
  name: string;
  role: string;
  quote: string;
  rating: number;
}

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 630;
const INSTA_SIZE = 1080;

// ─── Word-wrap helper that uses actual text measurement ────────────────────

function wrapQuote(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  lineHeight: number,
  startX: number,
  startY: number,
  align: 'left' | 'center' | 'right' = "left"
): number {
  const words = text.split(" ");
  let line = "";
  let y = startY;

  ctx.textAlign = align;

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + " ";
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && i > 0) {
      ctx.fillText(line.trim(), align === "center" ? startX + maxWidth / 2 : startX, y);
      line = words[i] + " ";
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), align === "center" ? startX + maxWidth / 2 : startX, y);
  return y + lineHeight;
}

// ─── Shared branding constants ─────────────────────────────────────────────

const COLORS = {
  bg: "#fff8f9",
  bgTop: "#ffffff",
  bgBottom: "#fef2f4",
  accent: "#ec4899",
  accentLight: "#fce7f3",
  green: "#22c55e",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  textPink: "#db2777",
  border: "#fbcfe8",
  gold: "#f59e0b",
};

// ─── Polyfill roundRect for older browsers / Readdy preview ─────────────────

function patchRoundRect(ctx: CanvasRenderingContext2D) {
  if (typeof ctx.roundRect !== "function") {
    ctx.roundRect = function(
      x: number, y: number, w: number, h: number, r: number | number[] = 0
    ) {
      const rad = typeof r === "number" ? r : r[0] ?? 0;
      const maxR = Math.min(rad, w / 2, h / 2);
      this.beginPath();
      this.moveTo(x + maxR, y);
      this.lineTo(x + w - maxR, y);
      this.quadraticCurveTo(x + w, y, x + w, y + maxR);
      this.lineTo(x + w, y + h - maxR);
      this.quadraticCurveTo(x + w, y + h, x + w - maxR, y + h);
      this.lineTo(x + maxR, y + h);
      this.quadraticCurveTo(x, y + h, x, y + h - maxR);
      this.lineTo(x, y + maxR);
      this.quadraticCurveTo(x, y, x + maxR, y);
      this.closePath();
    };
  }
}

// ─── Download a branded landscape share card ─────────────────────────────────

function downloadShareCard(review: ShareTarget) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  patchRoundRect(ctx);

  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
  grad.addColorStop(0, COLORS.bgTop);
  grad.addColorStop(1, COLORS.bgBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  // Decorative top border
  ctx.fillStyle = COLORS.accent;
  ctx.fillRect(0, 0, CANVAS_WIDTH, 8);

  // Decorative bottom accent line
  ctx.fillStyle = COLORS.green;
  ctx.fillRect(0, CANVAS_HEIGHT - 4, CANVAS_WIDTH, 4);

  // Stars
  ctx.fillStyle = COLORS.gold;
  ctx.font = "bold 36px serif";
  const stars = "\u2605".repeat(review.rating);
  ctx.fillText(stars, 80, 90);

  // Open quote mark (large decorative)
  ctx.fillStyle = COLORS.accentLight;
  ctx.font = "200px Georgia, serif";
  ctx.fillText("\u201C", 60, 220);

  // Quote text
  ctx.fillStyle = COLORS.textDark;
  ctx.font = "500 28px 'Inter', system-ui, sans-serif";
  const quoteY = wrapQuote(ctx, review.quote, CANVAS_WIDTH - 240, 46, 120, 190, "left");

  // Attribution divider
  const attrY = Math.max(quoteY + 40, 440);
  ctx.fillStyle = COLORS.border;
  ctx.fillRect(120, attrY - 16, 60, 3);

  // Name
  ctx.fillStyle = COLORS.textPink;
  ctx.font = "bold 24px 'Inter', system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(`\u2014 ${review.name}`, 120, attrY + 14);

  // Role
  ctx.fillStyle = COLORS.textMuted;
  ctx.font = "18px 'Inter', system-ui, sans-serif";
  ctx.fillText(review.role, 120, attrY + 44);

  // Brand name — bottom right
  ctx.fillStyle = COLORS.green;
  ctx.font = "bold 22px 'Inter', system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("Makers Alley Co.", CANVAS_WIDTH - 80, CANVAS_HEIGHT - 50);

  // Tagline under brand
  ctx.fillStyle = COLORS.textMuted;
  ctx.font = "14px 'Inter', system-ui, sans-serif";
  ctx.fillText("Handmade. Local. Every Day.", CANVAS_WIDTH - 80, CANVAS_HEIGHT - 26);

  // Trigger download
  const link = document.createElement("a");
  link.download = `makers-alley-review-${review.name.toLowerCase().replace(/\s+/g, "-")}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

// ─── Generate Instagram square post (1080x1080) ────────────────────────────

function downloadInstagramPost(review: ShareTarget) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  patchRoundRect(ctx);

  canvas.width = INSTA_SIZE;
  canvas.height = INSTA_SIZE;

  // Background — soft warm pink
  const grad = ctx.createLinearGradient(0, 0, INSTA_SIZE, INSTA_SIZE);
  grad.addColorStop(0, "#ffffff");
  grad.addColorStop(1, "#fdf2f8");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, INSTA_SIZE, INSTA_SIZE);

  // Decorative rounded-corner frame
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.roundRect(40, 40, INSTA_SIZE - 80, INSTA_SIZE - 80, 24);
  ctx.stroke();

  // Top brand strip
  ctx.fillStyle = COLORS.accent;
  ctx.beginPath();
  ctx.roundRect(40, 40, INSTA_SIZE - 80, 6, [24, 24, 0, 0]);
  ctx.fill();

  // Stars centered near top
  ctx.fillStyle = COLORS.gold;
  ctx.font = "bold 44px serif";
  ctx.textAlign = "center";
  const stars = "\u2605".repeat(review.rating);
  ctx.fillText(stars, INSTA_SIZE / 2, 160);

  // Large decorative quote mark
  ctx.fillStyle = COLORS.accentLight;
  ctx.font = "240px Georgia, serif";
  ctx.fillText("\u201C", INSTA_SIZE / 2, 320);

  // Quote text centered
  ctx.fillStyle = COLORS.textDark;
  ctx.font = "500 36px 'Inter', system-ui, sans-serif";
  const quoteY = wrapQuote(ctx, review.quote, INSTA_SIZE - 200, 56, 100, 400, "center");

  // Attribution centered
  const attrY = Math.max(quoteY + 60, 780);
  ctx.fillStyle = COLORS.textPink;
  ctx.font = "bold 32px 'Inter', system-ui, sans-serif";
  ctx.fillText(`\u2014 ${review.name}`, INSTA_SIZE / 2, attrY);

  ctx.fillStyle = COLORS.textMuted;
  ctx.font = "24px 'Inter', system-ui, sans-serif";
  ctx.fillText(review.role, INSTA_SIZE / 2, attrY + 38);

  // Bottom brand block
  ctx.fillStyle = COLORS.green;
  ctx.font = "bold 34px 'Inter', system-ui, sans-serif";
  ctx.fillText("Makers Alley Co.", INSTA_SIZE / 2, INSTA_SIZE - 120);

  ctx.fillStyle = COLORS.textMuted;
  ctx.font = "20px 'Inter', system-ui, sans-serif";
  ctx.fillText("@makersalley.co", INSTA_SIZE / 2, INSTA_SIZE - 88);

  ctx.fillStyle = COLORS.accent;
  ctx.font = "16px 'Inter', system-ui, sans-serif";
  ctx.fillText("Handmade \u00b7 Local \u00b7 Every Day", INSTA_SIZE / 2, INSTA_SIZE - 58);

  // Trigger download
  const link = document.createElement("a");
  link.download = `makers-alley-instagram-${review.name.toLowerCase().replace(/\s+/g, "-")}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

// ─── BlueSky share intent ──────────────────────────────────────────────────

function shareToBluesky(review: ShareTarget) {
  const pageUrl = typeof window !== "undefined" ? window.location.href : "https://makersalley.co/testimonials";
  const text = `${review.quote}\n\n\u2014 ${review.name}, ${review.role}\n\n${pageUrl}`;
  const url = `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

// ─── Facebook share intent ─────────────────────────────────────────────────

function shareToFacebook(review: ShareTarget) {
  const pageUrl = typeof window !== "undefined" ? window.location.href : "https://makersalley.co/testimonials";
  const quote = `${review.quote} \u2014 ${review.name}, ${review.role}`;
  const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(quote)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

// ─── Component ─────────────────────────────────────────────────────────────

export function SharePanel({ review, className = "" }: { review: ShareTarget; className?: string }) {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-50 hover:bg-pink-100 text-pink-500 transition-colors cursor-pointer"
        title="Share this review"
      >
        <i className="ri-share-line text-sm"></i>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute bottom-full right-0 mb-2 z-50 rounded-xl border p-1.5 w-48 shadow-lg bg-white border-gray-200">
            <button
              onClick={() => { shareToBluesky(review); close(); }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 w-full text-left cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#3b82f6" strokeWidth="2"/>
                  <path d="M8 14c0-2 2-4 4-4s4 2 4 4" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="9" cy="10" r="1" fill="#3b82f6"/>
                  <circle cx="15" cy="10" r="1" fill="#3b82f6"/>
                </svg>
              </div>
              <span className="font-medium">BlueSky</span>
            </button>

            <button
              onClick={() => { shareToFacebook(review); close(); }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 w-full text-left cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center text-blue-600">
                <i className="ri-facebook-circle-fill text-xl"></i>
              </div>
              <span className="font-medium">Facebook</span>
            </button>

            <div className="my-1 border-t border-gray-100" />

            <button
              onClick={() => { try { downloadInstagramPost(review); } catch(e) { console.error("Instagram card error:",e); } close(); }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 w-full text-left cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center text-pink-500">
                <i className="ri-instagram-line text-xl"></i>
              </div>
              <span className="font-medium">Instagram Post</span>
            </button>

            <button
              onClick={() => { try { downloadShareCard(review); } catch(e) { console.error("Share card error:",e); } close(); }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 w-full text-left cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center text-green-500">
                <i className="ri-download-line text-lg"></i>
              </div>
              <span className="font-medium">Download Card</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}